import { useContext, useEffect, useState, useRef } from "react";
import styles from "./VariousCourse.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { LanguageContext } from "../LanguageContext/LanguageContext";
const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;
import riyals from "./riyals.svg";
const VariousCourse = () => {
  const [courses, setCourses] = useState([]); // Initialize as an empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const contentRef = useRef(null);

  const { direction } = useContext(LanguageContext); // Check language direction (rtl or ltr)
  const navigate = useNavigate();
  const location = useLocation(); // To access URL parameters

  // Handle initial loading and data fetching
  useEffect(() => {
    console.log("Loading Status:", {
      coursesLoaded: courses.length > 0,
      documentReady: document.readyState,
      imagesLoaded,
    });

    const checkLoading = () => {
      if (
        courses.length > 0 &&
        document.readyState === "complete" &&
        imagesLoaded
      ) {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    };

    checkLoading();

    // Fallback to hide loader after 5 seconds
    const fallbackTimer = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, [courses, imagesLoaded]);

  // Check if all images are loaded
  useEffect(() => {
    if (contentRef.current) {
      const images = contentRef.current.getElementsByTagName("img");
      let loadedImages = 0;
      const totalImages = images.length;

      const imageLoaded = () => {
        loadedImages++;
        if (loadedImages === totalImages) {
          setImagesLoaded(true);
        }
      };

      if (totalImages === 0) {
        setImagesLoaded(true);
        return;
      }

      Array.from(images).forEach((img) => {
        if (img.complete) {
          imageLoaded();
        } else {
          img.addEventListener("load", imageLoaded);
          img.addEventListener("error", imageLoaded);
        }
      });

      return () => {
        Array.from(images).forEach((img) => {
          img.removeEventListener("load", imageLoaded);
          img.removeEventListener("error", imageLoaded);
        });
      };
    }
  }, [courses]);

  // Fetch courses data
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(
          `${marketingBackendUrl}/maflam/courses-light`
        );
        if (!response.ok) throw new Error("Failed to fetch courses");
        const data = await response.json();

        // Ensure data.courses is an array before sorting
        const sortedCourses = Array.isArray(data.courses)
          ? data.courses.sort((a, b) => {
              const rankingA = parseFloat(a.CourseRankingEnglish) || 0;
              const rankingB = parseFloat(b.CourseRankingEnglish) || 0;
              return rankingA - rankingB; // Descending order
            })
          : []; // If not an array, use an empty array

        // Filter courses based on URL category parameter and exclude 'BUNDLE' category
        const urlParams = new URLSearchParams(location.search);
        const categoryParam = urlParams.get("category"); // Get category parameter from URL

        // Exclude courses with CourseCategoryEnglish === 'BUNDLE'
        const filteredCourses = (
          categoryParam === "ALL" || !categoryParam
            ? sortedCourses
            : sortedCourses.filter(
                (course) => course.CourseCategoryEnglish === categoryParam
              )
        ).filter((course) => course.CourseCategoryEnglish !== "BUNDLE"); // Exclude 'BUNDLE' category

        setCourses(filteredCourses); // Update state with filtered or all courses
      } catch (err) {
        setError(err.message);
      }
    };

    fetchCourses();
  }, [location.search]); // Depend on location.search to re-fetch if the URL changes

  // Handle course click navigation
  const handleCourseClick = (courseId) => {
    navigate(`/home/R2/R4/filmproduction/${courseId}`);
  };

  const handleVideoHover = (video, isHovering) => {
    if (isHovering) {
      video.play().catch((error) => {});
    } else {
      video.pause();
      video.currentTime = 0; // Reset to start
    }
  };

  // Add this function to handle video loading
  const handleVideoLoad = (video) => {
    // Set current time to 5 seconds
    video.currentTime = 5;

    // Create a poster from the 5-second mark
    const createPoster = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(video, 0, 0, canvas.width, canvas.height);
      video.poster = canvas.toDataURL();
      // Reset video time to start
      video.currentTime = 0;
    };

    // Once we've seeked to 5 seconds, create the poster
    video.addEventListener("seeked", createPoster, { once: true });
  };

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <span className={styles.loader}></span>
      </div>
    );
  }

  if (error) return <p>Error loading data: {error}</p>;

  const buttonTitle = direction === "rtl" ? "تفاصيل الدورة" : "Course Details";

  return (
    <div className={styles.maincontainer} ref={contentRef}>
      <div className={styles.container}>
        <h2>
          {direction === "rtl"
            ? courses.length > 0
              ? "أو اختر من بين دوراتنا المتعددة"
              : "لا توجد دورات متاحة"
            : courses.length > 0
            ? "Or, choose from our various courses"
            : "No courses available"}
        </h2>
        <div className={styles.cardsContainer}>
          {courses.length > 0 ? (
            courses.map((course) => {
              const courseFields =
                direction === "rtl"
                  ? {
                      courseId: course.CourseIdArabic,
                      courseName: course.CourseNameArabic,
                      courseDescription: course.CourseDescriptionArabic,
                      thumbnail: course.ThumbnailArabic,
                      courseCategory: course.CourseCategoryArabic,
                      courseLevel: course.CourseLevelArabic,
                      estimatedLearningTime: course.EstimatedLearningTimeArabic,
                      courseCost: course.CourseCostArabic,
                      lectureCount: course.LectureCountArabic,
                      courseRanking: course.CourseRankingArabic,
                      courseInstructorName: course.CourseInstructorNameArabic,
                      courseWhatYouLearn: course.CourseWhatyouLearnArabic,
                      courseInstructorDetails:
                        course.CourseInstructorDetailsArabic,
                      courseInstructorPhoto: course.CourseInstructorPhotoArabic,
                      courseContent: course.CourseContentArabic,
                    }
                  : {
                      courseId: course.CourseIdEnglish,
                      courseName: course.CourseNameEnglish,
                      courseDescription: course.CourseDescriptionEnglish,
                      thumbnail: course.ThumbnailEnglish,
                      courseCategory: course.CourseCategoryEnglish,
                      courseLevel: course.CourseLevelEnglish,
                      estimatedLearningTime:
                        course.EstimatedLearningTimeEnglish,
                      courseCost: course.CourseCostEnglish,
                      lectureCount: course.LectureCountEnglish,
                      courseRanking: course.CourseRankingEnglish,
                      courseInstructorName: course.CourseInstructorNameEnglish,
                      courseWhatYouLearn: course.CourseWhatyouLearnEnglish,
                      courseInstructorDetails:
                        course.CourseInstructorDetailsEnglish,
                      courseInstructorPhoto:
                        course.CourseInstructorPhotoEnglish,
                      courseContent: course.CourseContentEnglish,
                    };

              const isInstructorDataAvailable =
                courseFields.courseInstructorName.toLowerCase() !== "na" &&
                courseFields.courseInstructorPhoto.toLowerCase() !== "na";

              // Check if thumbnail is a video file
              const isVideoThumbnail =
                courseFields.thumbnail?.match(/\.(mp4|webm|ogg)$/i);

              return (
                <div
                  style={{ overflow: "hidden" }}
                  key={course.courseId}
                  className={styles.card}
                >
                  {/* Separate containers for video and clickable content */}
                  <div style={{ overflow: "hidden" }}>
                    {isVideoThumbnail ? (
                      <video
                        style={{ width: "100%", objectFit: "cover" }}
                        controls
                        muted
                        loop
                        playsInline
                        defaultMuted
                        preload="auto"
                        className={styles.courseVideo}
                        onLoadedData={(e) => handleVideoLoad(e.target)}
                        onMouseEnter={(e) => handleVideoHover(e.target, true)}
                        onMouseLeave={(e) => handleVideoHover(e.target, false)}
                      >
                        <source
                          src={courseFields.thumbnail}
                          type={`video/${isVideoThumbnail[1]}`}
                        />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => handleCourseClick(course._id)}
                      >
                        <img
                          style={{ width: "100%" }}
                          src={courseFields.thumbnail || course.thumbnail}
                          alt={courseFields.courseName}
                        />
                      </div>
                    )}
                  </div>

                  {/* Instructor Details - Show only if available */}
                  {isInstructorDataAvailable && (
                    <div
                      style={{ margin: "25px", marginBottom: "0px" }}
                      className={styles.instructorInfo}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <img
                          src={
                            courseFields.courseInstructorPhoto ||
                            "/defaultInstructor.jpg"
                          }
                          alt={courseFields.courseInstructorName}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            marginRight: "10px",
                          }}
                        />
                        <p style={{ width: "auto" }}>
                          {courseFields.courseInstructorName}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className={styles.cardContent}>
                    {/* Make course name clickable */}
                    <h3
                      className={`${styles.title} ${styles.clickable}`}
                      onClick={() => handleCourseClick(course._id)}
                    >
                      {courseFields.courseName}
                    </h3>
                    <p className={styles.description}>
                      {courseFields.courseDescription?.slice(0, 270) || ""}...
                    </p>
                    <hr
                      style={{
                        color: "#39FFFB",
                        opacity: "0.3",
                        strokeWidth: "0.5px",
                      }}
                    />
                    <div className={styles.footer}>
                      <div className={styles.footerblocks}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            textAlign: "center",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          className={styles.footerblocks}
                        >
                          <span className={styles.price}>
                            {direction === "rtl" ? (
                              courseFields.courseCost === "0" ? (
                                "متاح فقط ضمن الباقة الكاملة"
                              ) : (
                                <>
                                  {courseFields.courseCost}{" "}
                                  <img
                                    src={riyals}
                                    alt="SAR"
                                    className={styles.riyalsIcon}
                                  />
                                </>
                              )
                            ) : courseFields.courseCost === "0" ? (
                              "Only Available with Bundle"
                            ) : (
                              <>
                                {courseFields.courseCost}{" "}
                                <img
                                  src={riyals}
                                  alt="SAR"
                                  className={styles.riyalsIcon}
                                />
                              </>
                            )}
                          </span>
                        </div>
                        <span className={styles.line}>|</span>
                        <div className={styles.footerblocks}>
                          &nbsp;
                          {/* <svg
                            xmlns="https://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path d="..." fill="#39FFFB" />
                            <path d="..." fill="#39FFFB" />
                          </svg> */}
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17316C0.00433284 8.00043 -0.1937 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8078C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17316C18.7362 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87103 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99112 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34314C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21446 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1571 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18Z"
                              fill="#39FFFB"
                            />
                            <path
                              d="M13.9998 9H10.9998V6C10.9998 5.73478 10.8944 5.48043 10.7069 5.29289C10.5193 5.10536 10.265 5 9.99976 5C9.73454 5 9.48019 5.10536 9.29265 5.29289C9.10511 5.48043 8.99976 5.73478 8.99976 6V10C8.99976 10.2652 9.10511 10.5196 9.29265 10.7071C9.48019 10.8946 9.73454 11 9.99976 11H13.9998C14.265 11 14.5193 10.8946 14.7069 10.7071C14.8944 10.5196 14.9998 10.2652 14.9998 10C14.9998 9.73478 14.8944 9.48043 14.7069 9.29289C14.5193 9.10536 14.265 9 13.9998 9Z"
                              fill="#39FFFB"
                            />
                          </svg>
                          &nbsp;
                          <p style={{ fontSize: "14px" }}>
                            {courseFields.estimatedLearningTime}
                          </p>
                        </div>
                      </div>
                      <button
                        className={styles.buyNow}
                        onClick={() => handleCourseClick(course._id)}
                      >
                        {buttonTitle}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No courses available</p>
          )}
        </div>
      </div>
    </div>
  );
};
//export variousCourse
export default VariousCourse;
